import React, { useState, useEffect, useRef } from "react";

function QuickNotes() {
  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000/";
  // Ajusta esta URL a la ruta de tu servidor de archivos (uploads)
  const API_UPLOAD_URL = process.env.REACT_APP_API_UPLOAD_URL || "http://localhost:3001";

  const [notes, setNotes] = useState([]);
  // Agregamos 'file' al estado inicial
  const [formData, setFormData] = useState({ id: null, name: "", body: "", file: null });
  // Estado para la previsualización local en el formulario
  const [previewUrl, setPreviewUrl] = useState(null); 
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchNotes();
    
  }, []);

  const fetchNotes = async () => {
    try {

       // 1. Obtenemos el usuario del localStorage
    const userStorage = localStorage.getItem("user");
    let userId = "";
    let userRole = "";

    if (userStorage) {
      try {
        // Convertimos el string JSON a un objeto y extraemos el id
        const userObj = JSON.parse(userStorage);
        userId = userObj.id;
        userRole = userObj.role;
      } catch (error) {
        console.error("Error al parsear el usuario de localStorage:", error);
      }
    }

    const urlQuickNotes = userRole === "admin"
      ? `${API_URL}quick-notes` // Admin ve todas las notas rápidas
      : `${API_URL}quick-notes/user/${userId}`; // Usuarios normales solo ven sus notas rápidas

      const response = await fetch(urlQuickNotes);
      if (!response.ok) throw new Error("Error al obtener las notas");
      const data = await response.json();
      setNotes(data);
    } catch (error) {
      console.error(error);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, file });
      // Si es una imagen, generamos una URL local temporal para mostrarla en el formulario
      if (file.type.startsWith("image/")) {
        setPreviewUrl(URL.createObjectURL(file));
      } else {
        setPreviewUrl("is_document"); // Bandera para saber que hay un documento adjunto
      }
    }
  };

  const removeFile = () => {
    setFormData({ ...formData, file: null });
    setPreviewUrl(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // 1. Obtenemos el usuario del localStorage
    const userStorage = localStorage.getItem("user");
    let userId = "";
    let userRole = "";

    if (userStorage) {
      try {
        // Convertimos el string JSON a un objeto y extraemos el id
        const userObj = JSON.parse(userStorage);
        userId = userObj.id;
        userRole = userObj.role;
      } catch (error) {
        console.error("Error al parsear el usuario de localStorage:", error);
      }
    }
   
    
    const endpoint = isEditing ? "quick-notes/edit" : "quick-notes/create";

    // 🚀 CAMBIO CLAVE: Usamos FormData para enviar el archivo
    const submitData = new FormData();
    if (formData.id) submitData.append("id", formData.id);
    submitData.append("name", formData.name);
    submitData.append("body", formData.body);
    submitData.append("userId", userId); // Aseguramos enviar el userId para asociar la nota al usuario correcto
    
    // Si el usuario seleccionó un archivo físico nuevo, lo adjuntamos
    if (formData.file) {
      submitData.append("mediaFile", formData.file);
    }

    // 🔥 AGREGA ESTAS 3 LÍNEAS AQUÍ:
    else if (isEditing && !previewUrl) {
      // Si estamos editando, NO hay archivo nuevo, y NO hay previewUrl (lo quitaron)
      submitData.append("removeMedia", "true");
    }

    try {
      const response = await fetch(`${API_URL}${endpoint}`, {
        method: "POST",
        // NO enviamos headers "Content-Type" para que el navegador configure el multipart/form-data
        body: submitData,
      });

      if (!response.ok) throw new Error("Error al guardar la nota");

      // Limpiamos el formulario
      setFormData({ id: null, name: "", body: "", file: null });
      setPreviewUrl(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      setIsEditing(false);
      
      await fetchNotes();
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Estás seguro de eliminar esta nota rápida?")) return;

    try {
      const response = await fetch(`${API_URL}quick-notes/delete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id }),
      });

      if (!response.ok) throw new Error("Error al eliminar la nota");
      await fetchNotes();
    } catch (error) {
      console.error(error);
    }
  };

  const handleEditClick = (note) => {
    setFormData({ id: note.id, name: note.name, body: note.body, file: null });
    
    // Si la nota ya tenía un archivo en la base de datos, lo mostramos
    if (note.media_url) {
      setPreviewUrl(`${API_UPLOAD_URL}${note.media_url}`);
    } else {
      setPreviewUrl(null);
    }
    
    setIsEditing(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleCancelEdit = () => {
    setFormData({ id: null, name: "", body: "", file: null });
    setPreviewUrl(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    setIsEditing(false);
  };

  function formatDate(timestamp) {
    if (!timestamp) return "Sin fecha";
    return new Date(timestamp).toLocaleString("es-CO", {
      day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  }

  // --- Función auxiliar para renderizar la miniatura en la lista ---
  const renderListThumbnail = (mediaUrl) => {
    if (!mediaUrl) return null;
    
    // Validamos rápidamente por la extensión si es imagen o documento
    const isImage = /\.(jpeg|jpg|gif|png|webp)$/i.test(mediaUrl);
    const isVideo = /\.(mp4|mov|avi|webm)$/i.test(mediaUrl);

    if (isImage) {
      return (
        <img 
          src={`${API_UPLOAD_URL}${mediaUrl}`} 
          alt="Adjunto" 
          style={styles.thumbnailImg} 
          crossOrigin="anonymous"
        />
      );
    } else if (isVideo) {
      // Convertido a enlace <a> para abrir en nueva pestaña
      return (
        <a 
          href={`${API_UPLOAD_URL}${mediaUrl}`} 
          target="_blank" 
          rel="noopener noreferrer" 
          style={{ ...styles.iconBox, textDecoration: "none" }}
        >
          🎥 Video
        </a>
      );
    } else {
      // Convertido a enlace <a> para que los documentos también se puedan ver/descargar
      return (
        <a 
          href={`${API_UPLOAD_URL}${mediaUrl}`} 
          target="_blank" 
          rel="noopener noreferrer" 
          style={{ ...styles.iconBox, textDecoration: "none" }}
        >
          📄 Doc
        </a>
      );
    }
  };

  return (
    <div className='quick-notes-page' style={styles.pageContainer}>
      <div style={styles.header}>
        <h1 style={styles.pageTitle}>Gestión de Notas Rápidas</h1>
        <p style={styles.pageSubtitle}>Crea plantillas con texto y archivos adjuntos.</p>
      </div>

      <div style={styles.splitLayout}>
        {/* COLUMNA IZQUIERDA: FORMULARIO */}
        <div style={styles.leftColumn}>
          <div style={styles.card}>
            <h2 style={styles.sectionTitle}>
              {isEditing ? "✏️ Editar Nota" : "✨ Crear Nueva Nota"}
            </h2>
            <form onSubmit={handleSubmit}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Nombre / Título:</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  style={styles.input}
                  placeholder="Ej: Saludo inicial"
                />
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>Cuerpo del mensaje:</label>
                <textarea
                  required={!formData.file && !previewUrl} // Requerido solo si no hay archivo
                  rows="5"
                  value={formData.body}
                  onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                  style={styles.textarea}
                  placeholder="Escribe el texto de la respuesta rápida..."
                />
              </div>

              {/* SECCIÓN DE ARCHIVO ADJUNTO */}
              <div style={styles.inputGroup}>
                <label style={styles.label}>Archivo Adjunto (Opcional):</label>
                
                {previewUrl && previewUrl !== "is_document" && (
                  <div style={styles.previewContainer}>
                    <img src={previewUrl} alt="Preview" style={styles.previewImg} crossOrigin="anonymous" />
                    <button type="button" onClick={removeFile} style={styles.removeBtn}>✕ Quitar</button>
                  </div>
                )}
                
                {previewUrl === "is_document" && (
                  <div style={styles.previewContainer}>
                    <span style={{ fontSize: '24px' }}>📄</span>
                    <span style={{ marginLeft: '10px' }}>{formData.file?.name}</span>
                    <button type="button" onClick={removeFile} style={styles.removeBtn}>✕ Quitar</button>
                  </div>
                )}

                {!previewUrl && (
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    accept="image/*,video/*,application/pdf,.doc,.docx,.xls,.xlsx"
                    style={{ ...styles.input, padding: '8px', cursor: 'pointer' }}
                  />
                )}
              </div>

              <div style={styles.buttonGroup}>
                <button type="submit" disabled={loading} style={styles.btnPrimary}>
                  {loading ? "Guardando..." : isEditing ? "Actualizar Nota" : "Crear Nota"}
                </button>
                {isEditing && (
                  <button type="button" onClick={handleCancelEdit} style={styles.btnSecondary}>
                    Cancelar
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>

        {/* COLUMNA DERECHA: LISTADO */}
        <div style={styles.rightColumn}>
          <div style={styles.card}>
            <h2 style={styles.sectionTitle}>📋 Mis Notas Guardadas</h2>
            {notes.length === 0 ? (
              <div style={styles.emptyState}>No tienes notas rápidas guardadas aún.</div>
            ) : (
              <ul style={styles.list}>
                {notes.sort((a, b) => a.name.localeCompare(b.name)).map((note) => (
                  <li key={note.id} style={styles.listItem}>
                    
                    {/* Renderizamos la miniatura si tiene archivo */}
                    {note.media_url && (
                      <div className="miniatura" style={styles.thumbnailWrapper}>
                        {renderListThumbnail(note.media_url)}
                      </div>
                    )}

                    <div style={styles.noteContent}>
                      <h4 style={styles.noteTitle}>{note.name}</h4>
                      <p style={styles.noteBody}>{note.body}</p>
                      <div style={styles.noteMeta}>
                        Creado: {formatDate(note.created_at)}
                      </div>
                    </div>

                    <div style={styles.actionButtons}>
                      <button onClick={() => handleEditClick(note)} style={styles.btnEdit}>Editar</button>
                      <button onClick={() => handleDelete(note.id)} style={styles.btnDelete}>Eliminar</button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ESTILOS
const styles = {
  // ... (Tus estilos anteriores se mantienen)
  pageContainer: { maxWidth: "1200px", margin: "0 auto", padding: "30px 20px", fontFamily: "system-ui, -apple-system, sans-serif", backgroundColor: "#f8fafc", minHeight: "100vh" },
  header: { marginBottom: "30px", borderBottom: "1px solid #e2e8f0", paddingBottom: "15px" },
  pageTitle: { margin: "0 0 5px 0", color: "#0f172a", fontSize: "28px" },
  pageSubtitle: { margin: "0", color: "#64748b", fontSize: "15px" },
  splitLayout: { display: "flex", gap: "30px", flexWrap: "wrap" },
  leftColumn: { flex: "1", minWidth: "320px", alignSelf: "flex-start" },
  rightColumn: { flex: "1.5", minWidth: "320px" },
  card: { backgroundColor: "#ffffff", borderRadius: "12px", padding: "25px", boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.05)", border: "1px solid #f1f5f9" },
  sectionTitle: { marginTop: "0", marginBottom: "20px", fontSize: "18px", color: "#1e293b", fontWeight: "600" },
  inputGroup: { marginBottom: "20px" },
  label: { display: "block", marginBottom: "8px", color: "#334155", fontSize: "14px", fontWeight: "500" },
  input: { width: "100%", padding: "12px", borderRadius: "8px", border: "1px solid #cbd5e1", fontSize: "15px", boxSizing: "border-box", outline: "none" },
  textarea: { width: "100%", padding: "12px", borderRadius: "8px", border: "1px solid #cbd5e1", fontSize: "15px", boxSizing: "border-box", outline: "none", resize: "vertical", fontFamily: "inherit" },
  buttonGroup: { display: "flex", gap: "10px", marginTop: "10px" },
  btnPrimary: { flex: "1", padding: "12px 20px", backgroundColor: "#2563eb", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: "600", fontSize: "15px" },
  btnSecondary: { padding: "12px 20px", backgroundColor: "#f1f5f9", color: "#475569", border: "1px solid #cbd5e1", borderRadius: "8px", cursor: "pointer", fontWeight: "600", fontSize: "15px" },
  emptyState: { padding: "40px 20px", textAlign: "center", color: "#94a3b8", backgroundColor: "#f8fafc", borderRadius: "8px", border: "1px dashed #cbd5e1" },
  list: { listStyle: "none", padding: "0", margin: "0", display: "flex", flexDirection: "column", gap: "15px", maxHeight: "calc(100vh - 280px)", overflowY: "auto", paddingRight: "10px" },
  listItem: { border: "1px solid #e2e8f0", padding: "15px", borderRadius: "10px", display: "flex", justifyContent: "space-between", alignItems: "flex-start", backgroundColor: "#fafaf9", gap: "15px" },
  noteContent: { flex: "1", paddingRight: "10px" },
  noteTitle: { margin: "0 0 8px 0", fontSize: "16px", color: "#0f172a", textTransform: "uppercase" },
  noteBody: { margin: "0 0 10px 0", color: "#475569", fontSize: "14px", whiteSpace: "pre-wrap" },
  noteMeta: { fontSize: "12px", color: "#94a3b8" },
  actionButtons: { display: "flex", flexDirection: "column", gap: "8px" },
  btnEdit: { padding: "8px 12px", backgroundColor: "#fdf08a", color: "#854d0e", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "13px", fontWeight: "600" },
  btnDelete: { padding: "8px 12px", backgroundColor: "#fee2e2", color: "#b91c1c", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "13px", fontWeight: "600" },

  // --- NUEVOS ESTILOS PARA IMÁGENES ---
  previewContainer: { position: "relative", display: "inline-flex", alignItems: "center", padding: "10px", border: "1px dashed #cbd5e1", borderRadius: "8px", backgroundColor: "#f8fafc", width: "100%", boxSizing: "border-box" },
  previewImg: { maxHeight: "150px", maxWidth: "100%", borderRadius: "6px", objectFit: "contain" },
  removeBtn: { marginLeft: "auto", backgroundColor: "#fee2e2", color: "#b91c1c", border: "none", borderRadius: "4px", padding: "5px 10px", cursor: "pointer", fontSize: "12px", fontWeight: "bold" },
  thumbnailWrapper: { width: "80px", height: "80px", flexShrink: 0, borderRadius: "6px", overflow: "hidden", border: "1px solid #e2e8f0", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#f1f5f9" },
  thumbnailImg: { width: "100%", height: "100%", objectFit: "cover" },
  iconBox: { fontSize: "12px", color: "#64748b", fontWeight: "bold", textAlign: "center", display: "flex", flexDirection: "column", gap: "4px" }
};

export default QuickNotes;